import { InfoCircleFilled } from '@ant-design/icons';
import AdConfigCRUDActions from '@mm/state/actions/AdConfigCRUDActions';
import { SearchEntityType } from '@mm/state/controllers/AdConfigController';
import {
  MMTargeting,
  PossibleLocation,
  TargetingAudience,
} from '@mm/state/model/AdConfigModel';
import {
  FBGeoLocationType,
  FBPlatform,
  FBPosition,
  FBPublisherPlatform,
  InstagramPosition,
  MessengerPosition,
} from '@mm/state/model/AdSetModel';
import { StoreState } from '@mm/state/store-interfaces';
import { ReactComponent as ClearSvg } from 'assets/messenger-ads-icons/clear.svg';
import { ReactComponent as MessengerIcon } from 'assets/sidebar-icons/fb-messenger-icon.svg';
import classnames from 'classnames';
import escapeStringRegexp from 'escape-string-regexp';
import {
  capitalize,
  intersection,
  isEmpty,
  isEqual,
  omit,
  range,
  union,
  uniq,
  without,
} from 'lodash';
import React, { ReactNode, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Label } from 'reactstrap';
import { Dispatch } from 'redux';
import Checkbox from '../../../../../../common/components/Checkbox';
import { RemoteSearchInput } from '../../../../../../common/components/RemoteSearchInput/RemoteSearchInput';
import SelectorWithArrow from '../../../../../../common/components/SelectorWithArrow/SelectorWithArrow';
import { ChannelIcon } from '../../../../liveChat/components/ChannelIcon';
import {
  FB_AGE_MAX,
  FB_AGE_MIN,
  FB_GENDER_FEMALE,
  FB_GENDER_MALE,
} from '../../../constants/constants';
import { usLocation } from './utils';

type RemoteAudience = { id: string; name: string } | string;
type MMAudience = { id: number; name: string };

type Props = {
  disabled: boolean;
  onChangeTargeting: (targeting: MMTargeting) => void;
  targeting: MMTargeting;
  remoteAudiences: RemoteAudience[];
  mmAudiences: MMAudience[];
  specialAdCategory: boolean;
  extended?: boolean;
  searchEntity?: { id?: number; type: SearchEntityType['type'] };
  usedInSlideout?: boolean;
};

const locationTypesMap: Record<FBGeoLocationType, string> = {
  country_group: 'Region',
  country: 'Country/Region',
  region: 'Region',
  large_geo_area: 'Region',
  medium_geo_area: 'Region',
  small_geo_area: 'Region',
  city: 'City',
  zip: 'Zip/Postal Code',
  electoral_district: 'Electoral District',
  subcity: 'City',
  neighborhood: 'Neighborhood',
  subneighborhood: 'Neighborhood',
  geo_market: 'Market',
  metro_area: 'Metro Area',
};

const prepareSuggestionType = (suggestion: PossibleLocation) => {
  const type = suggestion.type as FBGeoLocationType;
  const hierarchyName = capitalize(
    'geoHierarchyName' in suggestion ? suggestion.geoHierarchyName : '',
  );
  return hierarchyName || locationTypesMap[type];
};

const prepareAudienceSuggestionType = (isMm: boolean) =>
  isMm ? 'Customers.ai' : 'Facebook';

// Combines fields for different locations based on location position in hierarchy
// Example:
//   Zip Code 33001 maps to a specific city so we'd like to have something like:
//   33001, Default City, Default Region, Default Country
//   City Odessa maps to a specific region, so in this case we'll have something like:
//   Odessa, TX, US
const prepareSuggestionText = (suggestion: PossibleLocation) =>
  [
    suggestion.name,
    suggestion.type === 'geo_market' ? 'DMA' : '',
    'primaryCity' in suggestion && suggestion.primaryCity,
    'region' in suggestion && suggestion.region,
    suggestion.type !== 'country' &&
      (('countryCode' in suggestion && suggestion.countryCode) ||
        ('country' in suggestion && suggestion.country)),
  ]
    .filter(item => !!item)
    .join(', ');

const renderSuggestionWithType = (
  suggestionText: ReactNode,
  suggestionType?: ReactNode,
) => (
  <div className="ad-detail__selector-suggestion">
    <div className="ad-detail__selector-left-column">{suggestionText}</div>
    {suggestionType && (
      <div className="ad-detail__selector-right-column">{suggestionType}</div>
    )}
  </div>
);

const matchingQueryNodes = (query: string, text: string) => {
  const expression = new RegExp(escapeStringRegexp(query), 'ig');
  const { length } = query;
  const matches: [number, number][] = [];
  const htmlElements: ReactNode[] = [];
  let match = expression.exec(text);
  while (match) {
    matches.push([match.index, match.index + length]);
    match = expression.exec(text);
  }
  if (matches.length === 0) {
    return text;
  }
  const lastIndex = matches.reduce((acc, match) => {
    const [start, end] = match;
    htmlElements.push(text.slice(acc, start));
    htmlElements.push(<b>{text.slice(start, end)}</b>);
    return end;
  }, 0);
  htmlElements.push(text.slice(lastIndex));
  return htmlElements.filter(element => element !== '');
};

const renderAudienceSuggestion = (
  suggestion: TargetingAudience,
  query: string,
) => {
  const suggestionText = suggestion.name;
  const suggestionTextNodes =
    query === '' ? suggestionText : matchingQueryNodes(query, suggestionText);
  return renderSuggestionWithType(suggestionTextNodes);
};

const renderSuggestion = (suggestion: PossibleLocation, query: string) => {
  const suggestionText = prepareSuggestionText(suggestion);
  const suggestionType = prepareSuggestionType(suggestion);
  const suggestionTextNodes = matchingQueryNodes(query, suggestionText);
  return renderSuggestionWithType(suggestionTextNodes, suggestionType);
};

const filteredItems = (items: PossibleLocation[], item: PossibleLocation) => {
  const key = item.key;
  const itemRegionId =
    'regionId' in item ? (item.regionId || '').toString() : '';
  const itemCountryCode = 'countryCode' in item ? item.countryCode : '';
  const itemCountry = 'country' in item ? item.country : '';
  const itemCity =
    'primaryCityId' in item ? (item.primaryCityId || '').toString() : '';
  const filteredItems = items.filter((prevItem: PossibleLocation) => {
    const city =
      'primaryCityId' in prevItem
        ? (prevItem.primaryCityId || '').toString()
        : '';
    const regionId =
      'regionId' in prevItem ? (prevItem.regionId || '').toString() : '';
    const country = 'country' in prevItem ? prevItem.country : '';
    const countryCode = 'countryCode' in prevItem ? prevItem.countryCode : '';
    return (
      [regionId, country, countryCode, city].indexOf(key) === -1 &&
      [itemRegionId, itemCountry, itemCountryCode, itemCity].indexOf(
        prevItem.key,
      ) === -1
    );
  });
  return [...filteredItems, item];
};

const prepareMatchingAudiences = (
  mmAudiences: MMAudience[],
  remoteAudiences: RemoteAudience[],
  allAudiences: TargetingAudience[],
  query: string,
  mmAudiencesSelected: boolean,
) => {
  const expression =
    query === '' ? null : new RegExp(escapeStringRegexp(query), 'i');
  const availableAudiences = [
    ...(mmAudiencesSelected
      ? mmAudiences.map(aud => ({ ...aud, id: aud.id.toString(), mm: true }))
      : []),
    ...(!mmAudiencesSelected
      ? remoteAudiences.map(aud => {
          if (typeof aud === 'string') {
            return { id: aud, name: `Unknown audience (${aud})`, mm: false };
          }
          return { ...aud, mm: false };
        })
      : []),
  ];
  const filteredAudiences = availableAudiences.filter(
    aud =>
      allAudiences.find(
        targetingAud =>
          targetingAud.id === aud.id && targetingAud.mm === aud.mm,
      ) === undefined,
  );
  if (expression === null) {
    return filteredAudiences;
  }
  return filteredAudiences.filter(aud => expression.exec(aud.name));
};

const ageItemsBuilder = (min: number) =>
  range(min, FB_AGE_MAX + 1).map(item => ({
    id: item,
    name: item.toString(),
  }));
const minAgeItems = ageItemsBuilder(FB_AGE_MIN);
const genderChoices = [
  { id: 0, name: 'All' },
  { id: FB_GENDER_MALE, name: 'Male' },
  { id: FB_GENDER_FEMALE, name: 'Female' },
];
const inclusionTypes = [
  { id: 0, name: 'Include' },
  { id: 1, name: 'Exclude' },
];
const audienceTypes = [
  { id: 0, name: 'Customers.ai' },
  { id: 1, name: 'Facebook' },
];
const allDeviceTypes: FBPlatform[] = ['desktop', 'mobile'];
const mobileDeviceType: FBPlatform[] = ['mobile'];
const desktopDeviceType: FBPlatform[] = ['desktop'];

const allPlatforms: FBPublisherPlatform[] = [
  'instagram',
  'facebook',
  'messenger',
];
const allFBPositions: FBPosition[] = ['feed', 'marketplace'];
const allMessengerPositions: MessengerPosition[] = ['messenger_home'];
const allInstagramPositions: InstagramPosition[] = [
  'stream',
  'story',
  'explore',
];
const platformForPosition: { [key: string]: FBPublisherPlatform } = {
  facebookPositions: 'facebook',
  messengerPositions: 'messenger',
  instagramPositions: 'instagram',
};

const targetingPositionsEditAllowed = (targeting: MMTargeting) =>
  !!targeting.publisherPlatforms &&
  (targeting.publisherPlatforms.length > 1 ||
    (targeting.publisherPlatforms.length === 1 &&
      [
        ...(targeting.instagramPositions || []),
        ...(targeting.facebookPositions || []),
        ...(targeting.messengerPositions || []),
      ].length > 1));

const positionPresent = (positions: string[] | undefined, key: string) =>
  !!positions && positions.indexOf(key) !== -1;

export const Targeting = (props: Props) => {
  const {
    targeting,
    disabled,
    extended = true,
    onChangeTargeting,
    remoteAudiences,
    mmAudiences,
    specialAdCategory,
    searchEntity,
    usedInSlideout,
  } = props;
  const {
    ageMin,
    ageMax,
    genders,
    includedLocations,
    excludedLocations,
    audiences,
    excludedAudiences,
    devicePlatforms,
    publisherPlatforms,
    facebookPositions,
    messengerPositions,
    instagramPositions,
  } = targeting;
  const specialCategoryDisabled = disabled || specialAdCategory;
  const radioLabelClasses = classnames('ad-detail__radio-label', {
    'ad-detail__radio-label--disabled': disabled,
  });
  const fbFeedRequired =
    (!!facebookPositions && facebookPositions.indexOf('marketplace') !== -1) ||
    (!!messengerPositions &&
      messengerPositions.indexOf('messenger_home') !== -1);
  const instagramFeedRequired =
    !!instagramPositions && instagramPositions.indexOf('explore') !== -1;
  const maxAgeItems = ageItemsBuilder(ageMin);
  const searchResultsKey = specialAdCategory ? 'special' : 'regular';
  const [editIncluded, setEditIncluded] = useState(true);
  const [editIncludedAudiences, setEditIncludedAudiences] = useState(true);
  const [mmAudiencesSelected, setMMAudiencesSelected] = useState(true);
  const [locationsQuery, setLocationsQuery] = useState<string>('');
  const [audiencesFilter, setAudiencesFilter] = useState('');
  const [allSearchResults, setAllSearchResults] = useState<{
    special: { [key: string]: PossibleLocation[] };
    regular: { [key: string]: PossibleLocation[] };
  }>({ special: {}, regular: {} });
  const locationsKey =
    editIncluded || specialAdCategory
      ? 'includedLocations'
      : 'excludedLocations';
  const audiencesKey = editIncludedAudiences
    ? 'audiences'
    : 'excludedAudiences';

  const ARROW_SIZE = 14;
  const ARROW_COLOR = '#262626';

  const dispatch: Dispatch<StoreState> = useDispatch();
  const allAudiences = [...audiences, ...excludedAudiences];
  const matchingAudiences = prepareMatchingAudiences(
    mmAudiences,
    remoteAudiences,
    allAudiences,
    audiencesFilter,
    mmAudiencesSelected,
  );
  const locationResults = useMemo(
    () =>
      (allSearchResults[searchResultsKey][locationsQuery] || [])
        .filter(
          res =>
            ![...includedLocations, ...excludedLocations].find(
              item => item.key === res.key,
            ),
        )
        .slice(0, 10),
    [allSearchResults[searchResultsKey][locationsQuery]],
  );

  const onAudienceRemove = (
    audiences: TargetingAudience[],
    item: TargetingAudience,
    key: 'audiences' | 'excludedAudiences',
  ) => {
    onChangeTargeting({ ...targeting, [key]: without(audiences, item) });
  };

  const onLocationRemove = (
    locations: PossibleLocation[],
    item: PossibleLocation,
    key: 'includedLocations' | 'excludedLocations',
  ) => {
    let newLocations = without(locations, item);
    if (key === 'includedLocations' && isEmpty(newLocations)) {
      newLocations = [usLocation];
    }
    onChangeTargeting({ ...targeting, [key]: newLocations });
  };

  const onChangeDeviceType = (types: FBPlatform[]) => () => {
    const extraParams: Partial<MMTargeting> =
      types === desktopDeviceType
        ? {
            instagramPositions: [],
            messengerPositions: [],
            facebookPositions: intersection(
              union(targeting.facebookPositions || [], ['feed']),
              ['feed', 'marketplace'],
            ),
            publisherPlatforms: ['facebook'],
          }
        : {};
    onChangeTargeting({ ...targeting, ...extraParams, devicePlatforms: types });
  };

  const onSetAutoPlacement = (setAuto: boolean) => {
    if (setAuto) {
      onChangeTargeting(
        omit(
          targeting,
          'publisherPlatforms',
          'facebookPositions',
          'messengerPositions',
          'instagramPositions',
        ),
      );
    } else {
      onChangeTargeting({
        ...targeting,
        ...(targeting.devicePlatforms === desktopDeviceType
          ? {
              instagramPositions: [],
              messengerPositions: [],
              facebookPositions: ['feed', 'marketplace'],
              publisherPlatforms: ['facebook'],
            }
          : {
              publisherPlatforms: allPlatforms,
              facebookPositions: allFBPositions,
              messengerPositions: allMessengerPositions,
              instagramPositions: allInstagramPositions,
            }),
      });
    }
  };

  const allowEditPositions = (isOn: boolean, key: string, position: string) => {
    const isAllowed =
      !disabled && (targetingPositionsEditAllowed(targeting) || !isOn);
    if (!isAllowed) {
      return false;
    }
    if (targeting.devicePlatforms !== desktopDeviceType) {
      return true;
    }
    if (key !== 'facebookPositions') {
      return false;
    }
    return position !== 'story';
  };

  const onUpdatePositions =
    (
      key: 'facebookPositions' | 'instagramPositions' | 'messengerPositions',
      position: InstagramPosition | FBPosition | MessengerPosition,
    ) =>
    () => {
      const positions = targeting[key] || [];
      const on = positions.indexOf(position as any) === -1;
      const updates: Partial<MMTargeting> = {};
      if (on && position === 'messenger_home') {
        updates['messengerPositions'] = uniq([
          ...(positions as MessengerPosition[]),
          'messenger_home',
        ]);
        updates['facebookPositions'] = uniq([
          ...(targeting.facebookPositions || []),
          'feed',
        ]);
        updates['publisherPlatforms'] = uniq([
          ...(targeting.publisherPlatforms || []),
          'facebook',
          'messenger',
        ]);
      } else if (on && position === 'marketplace') {
        updates['facebookPositions'] = uniq([
          ...(positions as FBPosition[]),
          'feed',
          'marketplace',
        ]);
      } else if (on && position === 'explore') {
        updates['instagramPositions'] = uniq([
          ...(positions as InstagramPosition[]),
          'stream',
          'explore',
        ]);
      } else if (on) {
        updates[key] = [...positions, position] as any;
        updates['publisherPlatforms'] = uniq([
          ...(targeting.publisherPlatforms || []),
          platformForPosition[key],
        ]);
      } else {
        updates[key] = without(positions, position) as any;
        if (isEmpty(updates[key])) {
          const platforms = without(
            targeting.publisherPlatforms,
            platformForPosition[key],
          );
          return onChangeTargeting(
            omit({ ...targeting, publisherPlatforms: platforms }, key),
          );
        }
      }
      onChangeTargeting({ ...targeting, ...updates });
    };

  return (
    <>
      <div className="ad-detail__single-row-container">
        <div className="ad-config-detail__age-selector">
          <div className="ad-detail__field-title">Age</div>
          <div className="ad_detail__age-wrapper">
            <div className="ad-config-detail__age-dropdown ad-detail__selector">
              <SelectorWithArrow
                selectedItem={minAgeItems.find(item => item.id === ageMin)}
                onChange={(age: number) => {
                  onChangeTargeting({
                    ...targeting,
                    ageMin: age,
                    ageMax: Math.max(age, ageMax),
                  });
                }}
                disabled={specialCategoryDisabled}
                items={minAgeItems}
                arrowColor={ARROW_COLOR}
                arrowSize={ARROW_SIZE}
              />
            </div>
            <div className="ad-config-detail__targeting-age-separator">—</div>
            <div className="ad-config-detail__age-selector">
              <div className="ad-config-detail__age-dropdown ad-detail__selector">
                <SelectorWithArrow
                  selectedItem={maxAgeItems.find(item => item.id === ageMax)}
                  onChange={(age: number) => {
                    onChangeTargeting({ ...targeting, ageMax: age });
                  }}
                  disabled={specialCategoryDisabled}
                  items={maxAgeItems}
                  arrowColor={ARROW_COLOR}
                  arrowSize={ARROW_SIZE}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="ad-detail__gender-container">
          <div className="ad-detail__field-title">Gender</div>
          <div className="ad-config-detail__targeting-gender-dropdown ad-detail__selector">
            <SelectorWithArrow
              selectedItem={genderChoices.find(item =>
                genders === undefined ? item.id === 0 : item.id === genders[0],
              )}
              disabled={specialCategoryDisabled}
              onChange={(gender: number) => {
                if (gender === 0) {
                  onChangeTargeting(omit(targeting, 'genders'));
                } else {
                  onChangeTargeting({ ...targeting, genders: [gender] });
                }
              }}
              items={genderChoices}
              arrowColor={ARROW_COLOR}
              arrowSize={ARROW_SIZE}
            />
          </div>
        </div>
      </div>
      <hr className="ad-detail__separator" />
      <div className="ad-detail__field-title">Location</div>
      <div
        className={classnames('ad-detail__input-container', {
          'ad-detail__input-container-slideout': usedInSlideout,
        })}
      >
        <div className="ad-detail__field-subtitle">
          Target everyone in this location
        </div>
      </div>
      <div className="ad-detail__inputs-container">
        <div
          className={classnames('ad-detail__input-container', {
            'ad-detail__input-container-slideout': usedInSlideout,
          })}
        >
          <div className="ad-config-detail__inclusion-container">
            <div className="ad-config-detail__targeting-inclusion-dropdown ad-detail__selector">
              <SelectorWithArrow
                selectedItem={inclusionTypes.find(item =>
                  editIncluded || specialAdCategory
                    ? item.id === 0
                    : item.id === 1,
                )}
                disabled={specialCategoryDisabled}
                onChange={(inclusionType: number) => {
                  setEditIncluded(inclusionType === 0);
                }}
                items={inclusionTypes}
                arrowColor={ARROW_COLOR}
                arrowSize={ARROW_SIZE}
              />
            </div>
            <div className="ad-config-detail__inclusion-input-container">
              <div className="ad-detail__text-input-container">
                <RemoteSearchInput<PossibleLocation>
                  value=""
                  placeholder="Type to search for locations"
                  renderSuggestion={renderSuggestion}
                  onSelectedItem={item => {
                    onChangeTargeting({
                      ...targeting,
                      [locationsKey]: filteredItems(
                        targeting[locationsKey],
                        item,
                      ),
                    });
                  }}
                  lengthLimit={1}
                  disabled={disabled}
                  results={locationResults}
                  onFetch={async query => {
                    const rawResults: PossibleLocation[] =
                      allSearchResults[searchResultsKey][query] ||
                      (await dispatch(
                        AdConfigCRUDActions.search(
                          query,
                          specialAdCategory,
                          searchEntity?.id
                            ? (searchEntity as SearchEntityType)
                            : undefined,
                        ),
                      ));
                    setLocationsQuery(query);
                    if (!allSearchResults[searchResultsKey][query]) {
                      setAllSearchResults(prevSearchResults => ({
                        ...prevSearchResults,
                        [searchResultsKey]: {
                          ...allSearchResults[searchResultsKey],
                          [query]: rawResults,
                        },
                      }));
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="ad-config-detail__locations">
        {includedLocations.length > 0 && (
          <div className="ad-config-detail__locations-group">
            <div className="ad-config-detail__locations-label">Include:</div>
            <div className="ad-config-detail__locations-tags">
              {includedLocations.map((location, index) => (
                <div
                  className="ad-config-detail__tag ad-config-detail__included-tag"
                  key={index}
                >
                  <div>{`${prepareSuggestionText(
                    location,
                  )} (${prepareSuggestionType(location)})`}</div>
                  <div
                    className="ad-config-detail__location-remove"
                    onClick={() =>
                      onLocationRemove(
                        includedLocations,
                        location,
                        'includedLocations',
                      )
                    }
                  >
                    <ClearSvg />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {excludedLocations.length > 0 && (
          <div className="ad-config-detail__locations-group">
            <div className="ad-config-detail__locations-label">Exclude:</div>
            <div className="ad-config-detail__locations-tags">
              {excludedLocations.map((location, index) => (
                <div
                  className="ad-config-detail__tag ad-config-detail__excluded-tag"
                  key={index}
                >
                  <div>{`${prepareSuggestionText(
                    location,
                  )} (${prepareSuggestionType(location)})`}</div>
                  <div
                    className="ad-config-detail__location-remove"
                    onClick={() =>
                      onLocationRemove(
                        excludedLocations,
                        location,
                        'excludedLocations',
                      )
                    }
                  >
                    <ClearSvg />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      {extended && (
        <>
          <hr className="ad-detail__separator" />
          <div className="ad-detail__inputs-container">
            <div
              className={classnames('ad-detail__input-container', {
                'ad-detail__input-container-slideout': usedInSlideout,
              })}
            >
              <div className="ad-detail__field-title">Custom Audiences</div>
              <div className="ad-detail__subtext">
                Target people that fall into at least 1 of the following
                audiences
              </div>
              <div className="ad-config-detail__inclusion-container">
                <div className="ad-config-detail__targeting-inclusion-dropdown ad-detail__selector">
                  <SelectorWithArrow
                    selectedItem={inclusionTypes.find(item =>
                      editIncludedAudiences ? item.id === 0 : item.id === 1,
                    )}
                    disabled={disabled}
                    onChange={(inclusionType: number) => {
                      setEditIncludedAudiences(inclusionType === 0);
                    }}
                    items={inclusionTypes}
                    arrowColor={ARROW_COLOR}
                    arrowSize={ARROW_SIZE}
                  />
                </div>
                <div className="ad-config-detail__inclusion-input-container">
                  <div className="ad-detail__text-input-container ad-config-detail__audience-selection-container">
                    <RemoteSearchInput<TargetingAudience>
                      value=""
                      placeholder="Add audience"
                      renderSuggestion={renderAudienceSuggestion}
                      onSelectedItem={item => {
                        onChangeTargeting({
                          ...targeting,
                          [audiencesKey]: [...targeting[audiencesKey], item],
                        });
                      }}
                      disabled={disabled}
                      lengthLimit={0}
                      debounceTimeout={15}
                      results={matchingAudiences}
                      onFetch={async query => {
                        setAudiencesFilter(query);
                      }}
                    />
                    <div className="ad-config-detail__custom_audience_selector ad-detail__selector">
                      <SelectorWithArrow
                        selectedItem={audienceTypes.find(item =>
                          mmAudiencesSelected ? item.id === 0 : item.id === 1,
                        )}
                        disabled={disabled}
                        onChange={(audienceType: number) => {
                          setMMAudiencesSelected(audienceType === 0);
                        }}
                        items={audienceTypes}
                        arrowColor={ARROW_COLOR}
                        arrowSize={ARROW_SIZE}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="ad-config-detail__locations">
            {audiences.length > 0 && (
              <div className="ad-config-detail__locations-group">
                <div className="ad-config-detail__locations-label">
                  Include:
                </div>
                <div className="ad-config-detail__locations-tags">
                  {audiences.map((audience, index) => (
                    <div
                      className="ad-config-detail__tag ad-config-detail__included-tag"
                      key={index}
                    >
                      <div>{`${audience.name} (${prepareAudienceSuggestionType(
                        audience.mm,
                      )})`}</div>
                      <div
                        className="ad-config-detail__location-remove"
                        onClick={() =>
                          onAudienceRemove(audiences, audience, 'audiences')
                        }
                      >
                        <ClearSvg />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {excludedAudiences.length > 0 && (
              <div className="ad-config-detail__locations-group">
                <div className="ad-config-detail__locations-label">
                  Exclude:
                </div>
                <div className="ad-config-detail__locations-tags">
                  {excludedAudiences.map((audience, index) => (
                    <div
                      className="ad-config-detail__tag ad-config-detail__excluded-tag"
                      key={index}
                    >
                      <div>{`${audience.name} (${prepareAudienceSuggestionType(
                        audience.mm,
                      )})`}</div>
                      <div
                        className="ad-config-detail__location-remove"
                        onClick={() =>
                          onAudienceRemove(
                            excludedAudiences,
                            audience,
                            'excludedAudiences',
                          )
                        }
                      >
                        <ClearSvg />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <hr className="ad-detail__separator" />
          <div className="ad-config-detail__device-and-placement">
            <div className="ad-config-detail__targeting-devices">
              <div className="ad-detail__field-title">Device Type</div>
              <div className="ad-config-detail__device-type-choices">
                <div className="ad-config-detail__device-all ad-detail__radio-container">
                  <input
                    type="radio"
                    className="ad-detail__radio-input"
                    id="device-type-all-checker"
                    disabled={disabled}
                    checked={
                      isEmpty(devicePlatforms) ||
                      isEqual(
                        (devicePlatforms as FBPlatform[]).sort(),
                        allDeviceTypes,
                      )
                    }
                    onChange={onChangeDeviceType(allDeviceTypes)}
                  />
                  <Label
                    className={radioLabelClasses}
                    for="device-type-all-checker"
                  >
                    All (recommended)
                  </Label>
                </div>
                <div className="ad-config-detail__device-mobile ad-detail__radio-container">
                  <input
                    type="radio"
                    className="ad-detail__radio-input"
                    id="device-type-mobile-checker"
                    disabled={disabled}
                    checked={isEqual(devicePlatforms, mobileDeviceType)}
                    onChange={onChangeDeviceType(mobileDeviceType)}
                  />
                  <Label
                    className={radioLabelClasses}
                    for="device-type-mobile-checker"
                  >
                    Mobile
                  </Label>
                </div>
                <div className="ad-config-detail__device-desktop ad-detail__radio-container">
                  <input
                    type="radio"
                    className="ad-detail__radio-input"
                    id="device-type-desktop-checker"
                    disabled={disabled}
                    checked={isEqual(devicePlatforms, desktopDeviceType)}
                    onChange={onChangeDeviceType(desktopDeviceType)}
                  />
                  <Label
                    className={radioLabelClasses}
                    for="device-type-desktop-checker"
                  >
                    Desktop
                  </Label>
                </div>
              </div>
            </div>
            <div className="ad-config-detail__targeting-placement">
              <div className="ad-detail__field-title">Placement</div>
              <div className="ad-config-detail__placement-choices">
                <div className="ad-config-detail__placement-auto ad-detail__radio-container">
                  <input
                    type="radio"
                    className="ad-detail__radio-input"
                    id="placement-auto-checker"
                    disabled={disabled}
                    checked={publisherPlatforms === undefined}
                    onChange={() => onSetAutoPlacement(true)}
                  />
                  <Label
                    className={radioLabelClasses}
                    for="placement-auto-checker"
                  >
                    Automatic Placement
                  </Label>
                </div>
                <div className="ad-config-detail__edit-placement ad-detail__radio-container">
                  <input
                    type="radio"
                    className="ad-detail__radio-input"
                    id="edit-placement-checker"
                    disabled={disabled}
                    checked={publisherPlatforms !== undefined}
                    onChange={() => onSetAutoPlacement(false)}
                  />
                  <Label
                    className={radioLabelClasses}
                    for="edit-placement-checker"
                  >
                    Edit Placement
                  </Label>
                </div>
              </div>
            </div>
          </div>
          {publisherPlatforms !== undefined && (
            <>
              <hr className="ad-detail__separator" />
              <div className="ad-detail__field-title">Platforms</div>
              <div className="ad-config-detail__placements">
                <div className="ad-config-detail__facebook-positions ad-config-detail__positions-container">
                  <div className="ad-config-detail__placement-icon">
                    <ChannelIcon
                      className="ad-config-detail__placement-icon-svg"
                      channel="facebook"
                    />
                    <div>Facebook</div>
                  </div>
                  <div className="ad-config-detail__placement-checkboxes">
                    <Checkbox
                      className="ad-config-detail__placement-checkbox"
                      labelClassName="ad-config-detail__placement-checkbox-label"
                      isChecked={positionPresent(facebookPositions, 'feed')}
                      disabled={
                        !allowEditPositions(
                          positionPresent(facebookPositions, 'feed'),
                          'facebookPositions',
                          'feed',
                        ) || fbFeedRequired
                      }
                      onToggle={onUpdatePositions('facebookPositions', 'feed')}
                      label={
                        <div className="ad-config-detail__placement-label">
                          Feeds
                        </div>
                      }
                    />
                    <Checkbox
                      className="ad-config-detail__placement-checkbox"
                      labelClassName="ad-config-detail__placement-checkbox-label"
                      isChecked={positionPresent(
                        facebookPositions,
                        'marketplace',
                      )}
                      disabled={
                        !allowEditPositions(
                          positionPresent(facebookPositions, 'marketplace'),
                          'facebookPositions',
                          'marketplace',
                        )
                      }
                      onToggle={onUpdatePositions(
                        'facebookPositions',
                        'marketplace',
                      )}
                      label={
                        <div className="ad-config-detail__placement-label">
                          Marketplace
                        </div>
                      }
                    />
                  </div>
                </div>
                <div className="ad-config-detail__instagram-positions ad-config-detail__positions-container">
                  <div className="ad-config-detail__placement-icon">
                    <ChannelIcon
                      className="ad-config-detail__placement-icon-svg"
                      channel="instagram"
                    />
                    <div>Instagram</div>
                  </div>
                  <div className="ad-config-detail__placement-checkboxes">
                    <Checkbox
                      className="ad-config-detail__placement-checkbox"
                      labelClassName="ad-config-detail__placement-checkbox-label"
                      isChecked={positionPresent(instagramPositions, 'stream')}
                      disabled={
                        !allowEditPositions(
                          positionPresent(instagramPositions, 'stream'),
                          'instagramPositions',
                          'stream',
                        ) || instagramFeedRequired
                      }
                      onToggle={onUpdatePositions(
                        'instagramPositions',
                        'stream',
                      )}
                      label={
                        <div className="ad-config-detail__placement-label">
                          Feeds
                        </div>
                      }
                    />
                    <Checkbox
                      className="ad-config-detail__placement-checkbox"
                      labelClassName="ad-config-detail__placement-checkbox-label"
                      isChecked={positionPresent(instagramPositions, 'story')}
                      disabled={
                        !allowEditPositions(
                          positionPresent(instagramPositions, 'story'),
                          'instagramPositions',
                          'story',
                        )
                      }
                      onToggle={onUpdatePositions(
                        'instagramPositions',
                        'story',
                      )}
                      label={
                        <div className="ad-config-detail__placement-label">
                          Stories
                        </div>
                      }
                    />
                    <Checkbox
                      className="ad-config-detail__placement-checkbox"
                      labelClassName="ad-config-detail__placement-checkbox-label"
                      isChecked={positionPresent(instagramPositions, 'explore')}
                      disabled={
                        !allowEditPositions(
                          positionPresent(instagramPositions, 'explore'),
                          'instagramPositions',
                          'explore',
                        )
                      }
                      onToggle={onUpdatePositions(
                        'instagramPositions',
                        'explore',
                      )}
                      label={
                        <div className="ad-config-detail__placement-label">
                          Explore
                        </div>
                      }
                    />
                  </div>
                </div>
                <div className="ad-config-detail__messenger-positions ad-config-detail__positions-container">
                  <div className="ad-config-detail__placement-icon">
                    <MessengerIcon className="ad-config-detail__placement-icon-svg" />
                    <div>Messenger</div>
                  </div>
                  <div className="ad-config-detail__placement-checkboxes">
                    <Checkbox
                      className="ad-config-detail__placement-checkbox"
                      labelClassName="ad-config-detail__placement-checkbox-label"
                      isChecked={positionPresent(
                        messengerPositions,
                        'messenger_home',
                      )}
                      disabled={
                        !allowEditPositions(
                          positionPresent(messengerPositions, 'messenger_home'),
                          'messengerPositions',
                          'messenger_home',
                        )
                      }
                      onToggle={onUpdatePositions(
                        'messengerPositions',
                        'messenger_home',
                      )}
                      label={
                        <div className="ad-config-detail__placement-label">
                          Inbox
                        </div>
                      }
                    />
                  </div>
                </div>
              </div>
              <div className="ad-detail__subtext">
                <InfoCircleFilled className="ad-detail__info-circle" />
                <div>
                  <div>
                    {' '}
                    To use Facebook Marketplace or Messenger Inbox, Facebook
                    Feeds also has to be selected.
                  </div>

                  <div>
                    If you would like your ad shown on the Explore section of
                    Instagram, Instagram Feed also has to be selected.
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </>
  );
};
